import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Loader2, Brain } from 'lucide-react';
import { MarkdownBrief } from '../OntologyHelpers';

const DiffTab = ({
  diffFrom, setDiffFrom,
  diffTo, setDiffTo,
  diffClassId, setDiffClassId,
  diffRunning, diffResult,
  classes, onRunDiff,
}) => (
  <Card className="border-[#E5E7EB]">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-[#FF9500]" />Ontology Diff Viewer</CardTitle>
      <p className="text-xs text-[#6B7280] mt-1">
        Pick any time range (and optionally a class). The system replays the change log to show what was added, removed, renamed, or migrated, with GPT-5.2 governance commentary.
      </p>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] text-[#9CA3AF]">From (UTC)</Label>
          <Input className="h-8 text-xs" type="datetime-local" value={diffFrom} onChange={(e) => setDiffFrom(e.target.value)} data-testid="diff-from-input" />
        </div>
        <div>
          <Label className="text-[10px] text-[#9CA3AF]">To (UTC)</Label>
          <Input className="h-8 text-xs" type="datetime-local" value={diffTo} onChange={(e) => setDiffTo(e.target.value)} data-testid="diff-to-input" />
        </div>
        <div>
          <Label className="text-[10px] text-[#9CA3AF]">Class filter</Label>
          <Select value={diffClassId || 'all'} onValueChange={(v) => setDiffClassId(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs" data-testid="diff-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {classes.map(c => <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button className="h-8 text-xs bg-[#002FA7] hover:bg-[#001F6F] w-full" onClick={onRunDiff} disabled={diffRunning} data-testid="run-diff-btn">
            {diffRunning ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Computing…</> : <><Clock className="w-3.5 h-3.5 mr-1" />Compute Diff</>}
          </Button>
        </div>
      </div>

      {/* Quick presets */}
      <div className="flex flex-wrap gap-1.5" data-testid="diff-presets">
        {[
          { label: 'Last 24h', hours: 24 },
          { label: 'Last 7 days', hours: 24 * 7 },
          { label: 'Last 30 days', hours: 24 * 30 },
          { label: 'Last 90 days', hours: 24 * 90 },
        ].map(p => (
          <button key={p.label} type="button" className="text-[10px] px-2 py-1 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#4B5563]"
            onClick={() => {
              setDiffFrom(new Date(Date.now() - p.hours * 3600 * 1000).toISOString().slice(0, 16));
              setDiffTo(new Date().toISOString().slice(0, 16));
            }}>
            {p.label}
          </button>
        ))}
      </div>

      {diffResult && (
        <div className="space-y-3 pt-2" data-testid="diff-result">
          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="p-2 rounded bg-[#34C759]/10 border border-[#34C759]/30 text-xs"><p className="text-[9px] text-[#065F46] uppercase font-semibold">Classes +</p><p className="text-xl font-bold text-[#065F46]">{diffResult.totals.classes_added}</p></div>
            <div className="p-2 rounded bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-xs"><p className="text-[9px] text-[#991B1B] uppercase font-semibold">Classes −</p><p className="text-xl font-bold text-[#991B1B]">{diffResult.totals.classes_removed}</p></div>
            <div className="p-2 rounded bg-[#34C759]/10 border border-[#34C759]/30 text-xs"><p className="text-[9px] text-[#065F46] uppercase font-semibold">Individuals +</p><p className="text-xl font-bold text-[#065F46]">{diffResult.totals.individuals_added}</p></div>
            <div className="p-2 rounded bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-xs"><p className="text-[9px] text-[#991B1B] uppercase font-semibold">Individuals −</p><p className="text-xl font-bold text-[#991B1B]">{diffResult.totals.individuals_removed}</p></div>
            <div className="p-2 rounded bg-[#FF9500]/10 border border-[#FF9500]/30 text-xs"><p className="text-[9px] text-[#92400E] uppercase font-semibold">Renamed</p><p className="text-xl font-bold text-[#92400E]">{diffResult.totals.classes_renamed}</p></div>
            <div className="p-2 rounded bg-[#AF52DE]/10 border border-[#AF52DE]/30 text-xs"><p className="text-[9px] text-[#6B21A8] uppercase font-semibold">Migrated</p><p className="text-xl font-bold text-[#6B21A8]">{diffResult.totals.individuals_migrated}</p></div>
            <div className="p-2 rounded bg-[#34C759]/10 border border-[#34C759]/30 text-xs"><p className="text-[9px] text-[#065F46] uppercase font-semibold">Triples +</p><p className="text-xl font-bold text-[#065F46]">{diffResult.totals.triples_added}</p></div>
            <div className="p-2 rounded bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-xs"><p className="text-[9px] text-[#991B1B] uppercase font-semibold">Triples −</p><p className="text-xl font-bold text-[#991B1B]">{diffResult.totals.triples_removed}</p></div>
          </div>

          {/* Commentary */}
          {diffResult.commentary && (
            <div className="p-3 rounded-lg bg-[#F0F9FF] border border-[#BAE6FD]" data-testid="diff-commentary">
              <p className="text-xs font-medium text-[#0369A1] mb-1 flex items-center gap-1"><Brain className="w-3 h-3" />GPT-5.2 Governance Commentary</p>
              <MarkdownBrief markdown={diffResult.commentary} />
            </div>
          )}

          {/* Detail lists */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { title: 'Classes added', items: diffResult.classes_added, color: '#34C759', fmt: (x) => `${x.name}` },
              { title: 'Classes removed', items: diffResult.classes_removed, color: '#FF3B30', fmt: (x) => `${x.name}` },
              { title: 'Classes renamed', items: diffResult.classes_renamed, color: '#FF9500', fmt: (x) => `${x.from} → ${x.to}` },
              { title: 'Individuals added', items: diffResult.individuals_added, color: '#34C759', fmt: (x) => `${x.name} (${x.class_name || '?'})` },
              { title: 'Individuals removed', items: diffResult.individuals_removed, color: '#FF3B30', fmt: (x) => `${x.name} (${x.class_name || '?'})` },
              { title: 'Individuals migrated', items: diffResult.individuals_migrated, color: '#AF52DE', fmt: (x) => `${x.name}: ${x.from_class_name} → ${x.to_class_name}` },
              { title: 'Triples added', items: diffResult.triples_added, color: '#34C759', fmt: (x) => x.summary || `${x.subject} → ${x.object}` },
              { title: 'Triples removed', items: diffResult.triples_removed, color: '#FF3B30', fmt: (x) => x.summary || `${x.subject} → ${x.object}` },
            ].map(section => (
              <div key={section.title} className="p-2 rounded border border-[#E5E7EB]">
                <p className="text-xs font-semibold mb-1 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: section.color }} />
                  {section.title} ({section.items.length})
                </p>
                {section.items.length === 0 && <p className="text-[10px] text-[#9CA3AF]">— none</p>}
                <div className="space-y-0.5">
                  {section.items.slice(0, 8).map((item, i) => (
                    <div key={i} className="text-[11px] text-[#4B5563]">• {section.fmt(item)} <span className="text-[#9CA3AF]">· {item.user || 'system'} · {item.timestamp?.slice(0, 10)}</span></div>
                  ))}
                  {section.items.length > 8 && <p className="text-[10px] text-[#9CA3AF]">+{section.items.length - 8} more</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

export default DiffTab;
